// Search Analytics is finer on a URL-prefix property than on the domain property
// filtered to the same pages. These helpers pick that prefix when a page include
// points at it, and they keep the signup property as the boundary.
//
// Search Console includes a URL in a URL-prefix property only when the page
// string starts with the property string. A trailing slash is part of that
// string: https://example.com/blumen/ contains https://example.com/blumen/roses,
// and https://example.com/blumen/roses/ does not contain the same URL without
// the final slash. Adding a slash before comparing picks a property that
// cannot see the page, and the query comes back empty. The page URL itself is
// queried exactly as it was typed. Whether that URL ends with a slash is part
// of the site's URL structure, so a slash is not added or removed.

export type AccountProperty = {
  siteUrl: string;
  permissionLevel?: string;
};

export type QueryPropertyMatch = {
  /** Longest covering URL-prefix property. Preselected in the form. */
  suggested: string;
  /** Suggested first, then other covering prefixes, then the signup property. */
  options: string[];
};

type HttpProperty = {
  siteUrl: string;
  protocol: string;
  host: string;
  /** Directory path, with a trailing slash, used only to rank properties. */
  path: string;
  /** Path exactly as registered. A missing slash is not filled in. */
  rawPath: string;
};

type FilterTarget =
  | { kind: "url"; protocol: string; host: string; path: string }
  | { kind: "path"; path: string };

const INCLUDE_OPERATORS = new Set(["contains", "equals"]);

function canQueryPerformance(permissionLevel: string | undefined): boolean {
  const value = (permissionLevel || "").trim().toLowerCase();
  if (value === "") {
    return true;
  }
  return value === "siteowner" || value === "sitefulluser";
}

function canonicalPath(pathname: string): string {
  const path = pathname.startsWith("/") ? pathname : `/${pathname}`;
  if (path === "/") {
    return "/";
  }
  return path.endsWith("/") ? path : `${path}/`;
}

// Path as typed, before ? or #. URL.pathname decodes and resolves "." / ".."
// and that can add or drop a slash Search Console treats as a different prefix.
function rawPathOf(raw: string): string {
  const match = /^https?:\/\/[^/?#]*([^?#]*)/i.exec((raw || "").trim());
  if (!match || match[1] === "") {
    return "/";
  }
  return match[1].startsWith("/") ? match[1] : `/${match[1]}`;
}

/**
 * True when every URL under targetPath is inside the property path.
 * A property without a trailing slash still covers its folder
 * (…/blumen covers …/blumen/roses) and does not cover a neighbor (…/blumenstraeusse).
 * A property with a trailing slash covers only strings that already include that slash.
 */
function pathCoveredBy(propertyPath: string, targetPath: string): boolean {
  const prop = propertyPath.startsWith("/") ? propertyPath : `/${propertyPath}`;
  const target = targetPath.startsWith("/") ? targetPath : `/${targetPath}`;
  if (prop === "/") {
    return true;
  }
  if (prop.endsWith("/")) {
    return target.startsWith(prop);
  }
  return target === prop || target.startsWith(`${prop}/`);
}

function parseHttpProperty(raw: string): HttpProperty | null {
  const trimmed = (raw || "").trim();
  if (!/^https?:\/\//i.test(trimmed)) {
    return null;
  }
  try {
    const url = new URL(trimmed);
    if (url.protocol !== "http:" && url.protocol !== "https:") {
      return null;
    }
    const rawPath = rawPathOf(trimmed);
    return {
      siteUrl: trimmed,
      protocol: url.protocol.toLowerCase(),
      host: url.hostname.toLowerCase(),
      path: canonicalPath(rawPath),
      rawPath,
    };
  } catch {
    return null;
  }
}

function domainOf(raw: string): string {
  const trimmed = (raw || "").trim().toLowerCase();
  if (!trimmed.startsWith("sc-domain:")) {
    return "";
  }
  return trimmed.slice("sc-domain:".length).replace(/^\/+|\/+$/g, "");
}

function filterTarget(label: string): FilterTarget | null {
  const trimmed = (label || "").trim();
  if (trimmed === "") {
    return null;
  }
  if (/^https?:\/\//i.test(trimmed)) {
    const parsed = parseHttpProperty(trimmed);
    if (!parsed) {
      return null;
    }
    return {
      kind: "url",
      protocol: parsed.protocol,
      host: parsed.host,
      path: parsed.rawPath,
    };
  }
  if (!trimmed.startsWith("/")) {
    return null;
  }
  const path = trimmed.split("?")[0].split("#")[0];
  return { kind: "path", path: path.startsWith("/") ? path : `/${path}` };
}

function isInsideMain(mainProperty: string, candidate: HttpProperty): boolean {
  const domain = domainOf(mainProperty);
  if (domain) {
    return candidate.host === domain || candidate.host.endsWith(`.${domain}`);
  }
  const main = parseHttpProperty(mainProperty);
  if (!main) {
    return false;
  }
  return (
    candidate.protocol === main.protocol &&
    candidate.host === main.host &&
    candidate.path.startsWith(main.path)
  );
}

function isMoreSpecificThanMain(mainProperty: string, candidate: HttpProperty): boolean {
  if (domainOf(mainProperty)) {
    return true;
  }
  const main = parseHttpProperty(mainProperty);
  if (!main) {
    return false;
  }
  return candidate.path.length > main.path.length;
}

function covers(candidate: HttpProperty, target: FilterTarget): boolean {
  if (target.kind === "url") {
    if (candidate.protocol !== target.protocol || candidate.host !== target.host) {
      return false;
    }
  }
  return pathCoveredBy(candidate.rawPath, target.path);
}

function compareCandidates(mainHost: string) {
  return (a: HttpProperty, b: HttpProperty): number => {
    if (a.path.length !== b.path.length) {
      return b.path.length - a.path.length;
    }
    const aHost = mainHost !== "" && a.host === mainHost ? 1 : 0;
    const bHost = mainHost !== "" && b.host === mainHost ? 1 : 0;
    if (aHost !== bHost) {
      return bHost - aHost;
    }
    const aHttps = a.protocol === "https:" ? 1 : 0;
    const bHttps = b.protocol === "https:" ? 1 : 0;
    if (aHttps !== bHttps) {
      return bHttps - aHttps;
    }
    const aWww = a.host.startsWith("www.") ? 1 : 0;
    const bWww = b.host.startsWith("www.") ? 1 : 0;
    if (aWww !== bWww) {
      return bWww - aWww;
    }
    return a.siteUrl.localeCompare(b.siteUrl);
  };
}

/**
 * Property to query for a page include.
 * Returns null when the signup property is already the best match, or when the
 * filter is an exclude, a regex, a list, or text that is not a URL or path.
 */
export function matchQueryProperty(input: {
  mainProperty: string;
  properties: AccountProperty[];
  operator: string;
  label: string;
}): QueryPropertyMatch | null {
  const mainProperty = (input.mainProperty || "").trim();
  const operator = (input.operator || "").trim();
  if (mainProperty === "" || !INCLUDE_OPERATORS.has(operator)) {
    return null;
  }
  const target = filterTarget(input.label);
  if (!target) {
    return null;
  }

  const mainHttp = parseHttpProperty(mainProperty);
  const mainHost = mainHttp ? mainHttp.host : "";
  const seen = new Set<string>();
  const covering: HttpProperty[] = [];

  (input.properties || []).forEach((property) => {
    if (!canQueryPerformance(property?.permissionLevel)) {
      return;
    }
    const parsed = parseHttpProperty(property?.siteUrl || "");
    if (!parsed || !isInsideMain(mainProperty, parsed) || !covers(parsed, target)) {
      return;
    }
    if (!isMoreSpecificThanMain(mainProperty, parsed)) {
      return;
    }
    const key = `${parsed.protocol}//${parsed.host}${parsed.path}`;
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    covering.push(parsed);
  });

  if (covering.length === 0) {
    return null;
  }
  covering.sort(compareCandidates(mainHost));
  const options = covering.map((property) => property.siteUrl);
  if (!options.includes(mainProperty)) {
    options.push(mainProperty);
  }
  return { suggested: covering[0].siteUrl, options };
}

/** Longest prefix for one page URL, or the signup property when nothing is finer. */
export function propertyForPageUrl(
  mainProperty: string,
  properties: AccountProperty[],
  pageUrl: string
): string {
  const match = matchQueryProperty({
    mainProperty,
    properties,
    operator: "equals",
    label: pageUrl,
  });
  return propertySiteUrlForRequest(mainProperty, match, "");
}

/**
 * Site URL for the API request.
 * A prefix the user picked, or the suggestion, is sent unchanged.
 * When no prefix is finer than the signup property, that property is sent.
 * Leaving it off makes the API query a different site.
 */
export function propertySiteUrlForRequest(
  mainProperty: string,
  match: QueryPropertyMatch | null,
  selection: string
): string {
  const main = (mainProperty || "").trim();
  const suggested = match?.suggested || "";
  const picked = (selection || "").trim();
  const chosen = match?.options.includes(picked) ? picked : suggested;
  if (chosen !== "") {
    return chosen;
  }
  return main;
}

export type ContentCheckPageFilter = {
  /** equals on the URL as entered. Search Console compares the whole page string. */
  operator: "equals";
  /** The page URL as entered. Space is removed. A slash is not added or removed. */
  label: string;
};

/**
 * Page filter for a content check.
 * equals compares the typed URL with the page Search Console stored. The URL
 * is not shortened, and a slash is not added or removed.
 */
export function contentCheckPageFilter(pageUrl: string): ContentCheckPageFilter {
  return { operator: "equals", label: pageUrl.trim() };
}

/** One object or a list, and either camelCase or snake_case field names. */
export function accountPropertiesFromResponse(siteEntry: unknown): AccountProperty[] {
  const entries = Array.isArray(siteEntry)
    ? siteEntry
    : siteEntry && typeof siteEntry === "object"
      ? [siteEntry]
      : [];
  const properties: AccountProperty[] = [];
  entries.forEach((entry) => {
    if (!entry || typeof entry !== "object") {
      return;
    }
    const record = entry as Record<string, unknown>;
    const siteUrl = String(record.siteUrl ?? record.site_url ?? "").trim();
    if (siteUrl === "") {
      return;
    }
    const permission = record.permissionLevel ?? record.permission_level;
    properties.push({
      siteUrl,
      permissionLevel: permission == null ? undefined : String(permission),
    });
  });
  return properties;
}

/**
 * property_site_url for a content check of one page.
 * A finer URL-prefix is sent when one covers the page. Otherwise the signup
 * property is sent, including an sc-domain or a root URL-prefix. The site URL
 * Google returned is not rewritten, and the page URL is not rewritten either.
 */
export function contentCheckPropertySiteUrl(
  mainProperty: string,
  properties: AccountProperty[],
  pageUrl: string
): string {
  const match = matchQueryProperty({
    mainProperty,
    properties,
    operator: "equals",
    label: pageUrl,
  });
  return propertySiteUrlForRequest(mainProperty, match, "");
}

/** Property a report was queried with. An empty stored value means the signup property. */
export function reportQueryProperty(stored: string | undefined, mainProperty: string | undefined): string {
  const chosen = (stored || "").trim();
  if (chosen !== "") {
    return chosen;
  }
  return (mainProperty || "").trim();
}

/**
 * A page belongs to the project when it is on the domain property's host
 * (including subdomains) or under a URL-prefix property's folder.
 */
export function pageMatchesProjectProperty(pageURL: string, property: string): boolean {
  const page = parseHttpProperty(pageURL);
  if (!page) {
    return false;
  }
  const domain = domainOf(property);
  if (domain) {
    return page.host === domain || page.host.endsWith(`.${domain}`);
  }
  const prefix = parseHttpProperty(property);
  if (!prefix) {
    return false;
  }
  return (
    page.protocol === prefix.protocol &&
    page.host === prefix.host &&
    pathCoveredBy(prefix.rawPath, page.rawPath)
  );
}
